import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface ClassData {
  id: number;
  class_name: string;
  grade_name: string;
  year_name: string;
  assigned_teachers: string | null;
}

interface FormData {
  grades: { id: number; grade_name: string }[];
  academic_years: { id: number; year_name: string }[];
  teachers: { id: number; first_name: string; last_name: string }[];
}

const ManageClasses: React.FC = () => {
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Create Class Modal
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  const [className, setClassName] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [yearId, setYearId] = useState('');

  // Assign Teacher Modal
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');

  const fetchData = async () => {
    try {
      const [classRes, formRes] = await Promise.all([
        api.get('/manage-classes'),
        api.get('/manage-classes/form-data')
      ]);
      if (classRes.data.success) setClasses(classRes.data.data);
      if (formRes.data.success) setFormData(formRes.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateClass = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/manage-classes', {
        class_name: className,
        grade_id: gradeId,
        academic_year_id: yearId
      });
      setIsClassModalOpen(false);
      setClassName('');
      fetchData();
    } catch (err) {
      alert("Failed to create class.");
    }
  };

  const handleAssignTeacher = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/manage-classes/assign', {
        class_id: selectedClassId,
        teacher_id: selectedTeacherId
      });
      setIsAssignModalOpen(false);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to assign teacher.");
    }
  };

  const openAssignModal = (classId: number) => {
    setSelectedClassId(classId);
    setSelectedTeacherId('');
    setIsAssignModalOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      
      {/* Header controls banner */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-page-header mb-1">Manage Classes</h1>
          <p className="text-helper">Create academic class streams for the year and assign teachers to their respective subject junctions.</p>
        </div>
        <button 
          onClick={() => setIsClassModalOpen(true)} 
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-sm text-nav text-center whitespace-nowrap"
        >
          + Create Class
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* A. Responsive Card view for Mobile viewports */}
            <div className="block md:hidden p-4 space-y-3.5">
              {classes.length === 0 ? (
                <p className="text-center text-helper italic text-slate-400 py-6">No classes found.</p>
              ) : (
                classes.map((c) => (
                  <div key={c.id} className="p-4 rounded-xl border border-slate-100 bg-white space-y-3">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-card-title text-indigo-900">{c.class_name}</span>
                        <span className="px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-[9px] font-bold uppercase tracking-wider text-slate-600">
                          {c.year_name}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500">Grade Level: <strong className="text-slate-700">{c.grade_name}</strong></p>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-slate-50">
                      <div className="text-[9px]">
                        <span className="text-slate-400 block font-bold uppercase tracking-wider">Assigned Teachers</span>
                        {c.assigned_teachers ? (
                          <span className="text-emerald-600 font-bold">{c.assigned_teachers}</span>
                        ) : (
                          <span className="text-slate-400 italic">None</span>
                        )}
                      </div>
                      
                      <button 
                        onClick={() => openAssignModal(c.id)} 
                        className="text-indigo-600 font-semibold hover:underline text-nav"
                      >
                        Assign Teacher
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* B. Wide Table view for Desktop/Tablet viewports */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Class Name</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Grade</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Academic Year</th>
                    <th className="p-4 text-left text-card-title text-slate-400 uppercase tracking-wider">Assigned Teachers</th>
                    <th className="p-4 text-center text-card-title text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {classes.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-helper italic text-slate-400">No classes found.</td>
                    </tr>
                  ) : (
                    classes.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="p-4 text-table-body font-bold text-slate-900">{c.class_name}</td>
                        <td className="p-4 text-table-body text-slate-500">{c.grade_name}</td>
                        <td className="p-4 text-table-body text-slate-500">{c.year_name}</td>
                        <td className="p-4">
                          {c.assigned_teachers ? (
                            <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border border-emerald-150">{c.assigned_teachers}</span>
                          ) : (
                            <span className="text-slate-400 italic text-[10px]">Unassigned</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <button onClick={() => openAssignModal(c.id)} className="text-indigo-600 font-semibold hover:underline text-nav">Assign Teacher</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Create Class Modal */}
      {isClassModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-100">
            <h2 className="text-section-header mb-4">Create New Class</h2>
            <form onSubmit={handleCreateClass} className="space-y-4">
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Class Name (e.g. 10A)</label>
                <input type="text" value={className} onChange={e => setClassName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required />
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Grade Level</label>
                <select value={gradeId} onChange={e => setGradeId(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required>
                  <option value="">Select Grade</option>
                  {formData?.grades.map(g => (
                    <option key={g.id} value={g.id}>{g.grade_name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Academic Year</label>
                <select value={yearId} onChange={e => setYearId(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required>
                  <option value="">Select Year</option>
                  {formData?.academic_years.map(y => (
                    <option key={y.id} value={y.id}>{y.year_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setIsClassModalOpen(false)} className="px-4 py-2 text-nav font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button type="submit" className="px-5 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm transition-colors">Save Class</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Teacher Modal */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-100">
            <h2 className="text-section-header mb-4 font-bold text-slate-900">Assign Teacher to Class</h2>
            <form onSubmit={handleAssignTeacher} className="space-y-4">
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Select Teacher</label>
                <select value={selectedTeacherId} onChange={e => setSelectedTeacherId(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required>
                  <option value="">Choose a teacher</option>
                  {formData?.teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setIsAssignModalOpen(false)} className="px-4 py-2 text-nav font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button type="submit" className="px-5 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm transition-colors">Assign</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageClasses;
